import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  RefreshCw, Sun, Moon, Filter, Search, X,
  AlertTriangle, DollarSign, Clock, FileText,
} from "lucide-react";
import { getDadosInadimplencia } from "../../api";
import SideBarNavFaturamento from '../../componets/SideBarNavFaturamento';
import SideBarNavDash from '../../componets/SideBarNavDash';
import { useLocation } from 'react-router-dom';

import { 
  BarChart, Bar, LineChart, Line, XAxis, YAxis, 
  CartesianGrid, Tooltip, ResponsiveContainer, Legend 
} from 'recharts';

/* ─── Temas ─────────────────────────────────────────────────────────── */
const LIGHT = {
  bg: "#F5F3FF", card: "#FFFFFF", border: "#E5E7EB",
  text: "#1F1235", muted: "#6B7280", brand: "#7C3AED",
  row1: "#FFFFFF", row2: "#FAFAFA", chartGrid: "#E5E7EB"
};
const DARK = {
  bg: "#0E0A1F", card: "#17102E", border: "#2D1F4E",
  text: "#F3F0FF", muted: "#9CA3AF", brand: "#A78BFA",
  row1: "#17102E", row2: "#1C143A", chartGrid: "#2D1F4E"
};

/* ─── Helpers ────────────────────────────────────────────────────────── */
const PAGE_SIZE = 15;

function parseBRL(v) {
  if (typeof v === "number") return v;
  let str = String(v ?? "").replace(/[^0-9.,-]/g, "");
  if (str.includes(",")) {
    str = str.replace(/\./g, "").replace(",", ".");
  }
  return parseFloat(str) || 0;
}

function fmtBRL(v) {
  const n = parseBRL(v);
  if (isNaN(n)) return "—";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function parseDias(v) {
  const d = parseInt(v, 10);
  return isNaN(d) ? NaN : Math.abs(d);
}

function diasColor(dias) {
  const d = parseDias(dias);
  if (isNaN(d)) return "#6B7280";
  if (d <= 30) return "#F59E0B";
  if (d <= 60) return "#EF4444";
  return "#991B1B";
}

function situacaoBadge(sit, C) {
  const s = (sit ?? "").toLowerCase();
  if (s.includes("pago")) return { bg: "#D1FAE5", color: "#065F46", label: sit };
  if (s.includes("canc")) return { bg: "#FEE2E2", color: "#991B1B", label: sit };
  if (s.includes("parcial")) return { bg: "#FEF3C7", color: "#92400E", label: sit };
  return { bg: "#EDE9FE", color: "#5B21B6", label: sit || "—" };
}

function parseDataBr(str) {
  if (!str) return null;
  const parts = str.split('/');
  if (parts.length !== 3) return null;
  const [d, m, y] = parts;
  return new Date(y, m - 1, d);
}

/* ─── Subcomponentes ─────────────────────────────────────────────────── */
function ThSort({ label, col, sort, onSort, align = "left" }) {
  const active = sort.col === col;
  return (
    <th
      onClick={() => onSort(col)}
      style={{
        padding: "10px 14px", fontSize: 11, fontWeight: 700, textTransform: "uppercase",
        letterSpacing: "0.06em", cursor: "pointer", userSelect: "none",
        textAlign: align, whiteSpace: "nowrap",
        color: active ? "#7C3AED" : "#9CA3AF",
        borderBottom: "2px solid #E5E7EB",
        background: "transparent",
      }}
    >
      {label} {active ? (sort.dir === "asc" ? "↑" : "↓") : ""}
    </th>
  );
}

function KpiCard({ icon: Icon, label, value, sub, color, C }) {
  return (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`, borderRadius: 10,
      padding: "14px 16px", display: "flex", alignItems: "center", gap: 12,
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: 8,
        background: color + "20", display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <Icon size={18} color={color} />
      </div>
      <div>
        <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
        <div style={{ fontSize: 20, fontWeight: 800, color: C.text, lineHeight: 1.2 }}>{value}</div>
        {sub && <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{sub}</div>}
      </div>
    </div>
  );
}

function MultiSelect({ options, value, onChange, placeholder, C }) {
  const [open, setOpen] = useState(false);
  const toggle = (v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  return (
    <div style={{ position: "relative" }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          width: "100%", padding: "7px 10px", borderRadius: 7, fontSize: 12,
          border: `1px solid ${C.border}`, background: C.card, color: value.length ? C.text : C.muted,
          textAlign: "left", cursor: "pointer", display: "flex", justifyContent: "space-between",
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "90%" }}>
          {value.length ? value.join(", ") : placeholder}
        </span>
        <span style={{ fontSize: 10 }}>▾</span>
      </button>
      {open && (
        <div style={{
          position: "absolute", top: "100%", left: 0, right: 0, zIndex: 99,
          background: C.card, border: `1px solid ${C.border}`, borderRadius: 7,
          maxHeight: 200, overflowY: "auto", boxShadow: "0 4px 12px rgba(0,0,0,0.12)", marginTop: 2,
        }}>
          {options.map((o) => (
            <label key={o} style={{
              display: "flex", alignItems: "center", gap: 8, padding: "7px 10px",
              cursor: "pointer", fontSize: 12, color: C.text,
            }}>
              <input type="checkbox" checked={value.includes(o)} onChange={() => toggle(o)}
                style={{ accentColor: C.brand }} />
              {o}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function DateFilter({ label, startValue, endValue, onChangeStart, onChangeEnd, C }) {
  return (
    <div>
      <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4, fontWeight: 600 }}>{label}</label>
      <div style={{ display: "flex", gap: 4 }}>
        <input 
          type="date" 
          value={startValue} 
          onChange={(e) => onChangeStart(e.target.value)}
          style={{ width: "50%", padding: "6px", borderRadius: 7, fontSize: 11, border: `1px solid ${C.border}`, background: C.card, color: C.text }} 
        />
        <input 
          type="date" 
          value={endValue} 
          onChange={(e) => onChangeEnd(e.target.value)}
          style={{ width: "50%", padding: "6px", borderRadius: 7, fontSize: 11, border: `1px solid ${C.border}`, background: C.card, color: C.text }} 
        />
      </div>
    </div>
  );
}

/* ─── Componente Principal ───────────────────────────────────────────── */
export default function Inadimplencia() {
  const navigate = useNavigate();
  const [isLight, setIsLight] = useState(true);
  const C = isLight ? LIGHT : DARK;

  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);

  const [sort, setSort] = useState({ col: "Dias em Atraso", dir: "desc" });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  
  // Filtros atualizados
  const [filters, setFilters] = useState({ 
    cliente: [], gp: [], 
    emissaoInicio: "", emissaoFim: "",
    expectInicio: "", expectFim: ""
  });

  /* Carregar dados */
  useEffect(() => {
    setLoading(true);
    getDadosInadimplencia()
      .then((res) => {
        setDados(res.data ?? []);
        setLastUpdate(new Date().toLocaleTimeString("pt-BR"));
      })
      .finally(() => setLoading(false));
  }, []);

  /* Opções únicas para filtros */
  const opts = useMemo(() => ({
    cliente: [...new Set(dados.map((r) => r["Nome Cliente"]).filter(Boolean))].sort(),
    gp: [...new Set(dados.map((r) => r["Gerente de Projeto"]).filter(Boolean))].sort(),
  }), [dados]);

  /* Filtro + busca */
  const filtrado = useMemo(() => {
    let rows = [...dados];

    // Filtros MultiSelect
    if (filters.cliente.length) rows = rows.filter((r) => filters.cliente.includes(r["Nome Cliente"]));
    if (filters.gp.length) rows = rows.filter((r) => filters.gp.includes(r["Gerente de Projeto"]));
    
    // Filtros de Data
    if (filters.emissaoInicio) {
      const start = new Date(`${filters.emissaoInicio}T00:00:00`).getTime();
      rows = rows.filter(r => { const d = parseDataBr(r["Emissão"]); return d && d.getTime() >= start; });
    }
    if (filters.emissaoFim) {
      const end = new Date(`${filters.emissaoFim}T23:59:59`).getTime();
      rows = rows.filter(r => { const d = parseDataBr(r["Emissão"]); return d && d.getTime() <= end; });
    }
    if (filters.expectInicio) {
      const start = new Date(`${filters.expectInicio}T00:00:00`).getTime();
      rows = rows.filter(r => { const d = parseDataBr(r["Expectativa recebimento"]); return d && d.getTime() >= start; });
    }
    if (filters.expectFim) {
      const end = new Date(`${filters.expectFim}T23:59:59`).getTime();
      rows = rows.filter(r => { const d = parseDataBr(r["Expectativa recebimento"]); return d && d.getTime() <= end; });
    }

    // Busca livre
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter((r) => Object.values(r).some((v) => String(v ?? "").toLowerCase().includes(q)));
    }
    return rows;
  }, [dados, filters, search]);

  const totalProjetosUnicos = useMemo(() => {
    return new Set(filtrado.map(r => r["Nome do Projeto"])).size;
    }, [filtrado]);
  
  /* Ordenação */
  const sorted = useMemo(() => {
    return [...filtrado].sort((a, b) => {
      const va = a[sort.col] ?? "";
      const vb = b[sort.col] ?? "";
      const na = parseBRL(va);
      const nb = parseBRL(vb);
      if (!isNaN(na) && !isNaN(nb)) return sort.dir === "asc" ? na - nb : nb - na;
      return sort.dir === "asc"
        ? String(va).localeCompare(String(vb), "pt-BR")
        : String(vb).localeCompare(String(va), "pt-BR");
    });
  }, [filtrado, sort]);

  /* Paginação */
  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const pageData = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleSort = (col) => {
    setSort((s) => ({ col, dir: s.col === col && s.dir === "asc" ? "desc" : "asc" }));
    setPage(1);
  };

  const activeFilters = filters.cliente.length + filters.gp.length + 
    (filters.emissaoInicio ? 1 : 0) + (filters.emissaoFim ? 1 : 0) + 
    (filters.expectInicio ? 1 : 0) + (filters.expectFim ? 1 : 0);

  const limpar = () => { 
    setFilters({ cliente: [], gp: [], emissaoInicio: "", emissaoFim: "", expectInicio: "", expectFim: "" }); 
    setSearch(""); setPage(1); 
  };

  /* KPIs */
  const totalValor = useMemo(() => filtrado.reduce((acc, r) => acc + (parseBRL(r["Valor Bruto"]) || 0), 0), [filtrado]);
  const mediaAtraso = useMemo(() => {
      const validos = filtrado.map((r) => parseDias(r["Dias em Atraso"])).filter((d) => !isNaN(d));
      return validos.length ? Math.round(validos.reduce((a, b) => a + b, 0) / validos.length) : 0;
  }, [filtrado]);
  const criticos = filtrado.filter((r) => parseDias(r["Dias em Atraso"]) > 60).length;

  /* ====================================================================
     DADOS PARA OS GRÁFICOS (RECHARTS)
  ==================================================================== */
  
  // 1. Gráfico: Valor Bruto por Projeto (Top 10)
  const chartDataProjeto = useMemo(() => {
    const map = {};
    filtrado.forEach(r => {
      const proj = r["Nome do Projeto"] || "N/I";
      map[proj] = (map[proj] || 0) + (parseBRL(r["Valor Bruto"]) || 0);
    });
    return Object.entries(map)
      .map(([name, valor]) => ({ name, valor }))
      .sort((a, b) => b.valor - a.valor)
      .slice(0, 10);
  }, [filtrado]);

  // 2. Gráfico: Valor Bruto por GP (Top 10)
  const chartDataGP = useMemo(() => {
    const map = {};
    filtrado.forEach(r => {
      const gp = r["Gerente de Projeto"] || "N/I";
      map[gp] = (map[gp] || 0) + (parseBRL(r["Valor Bruto"]) || 0);
    });
    return Object.entries(map)
      .map(([name, valor]) => ({ name, valor }))
      .sort((a, b) => b.valor - a.valor)
      .slice(0, 10);
  }, [filtrado]);

  // 3. Gráfico: Dias em Atraso por Nota e Projeto (Top 15 mais atrasadas)
  const chartDataAtraso = useMemo(() => {
    return filtrado.map(r => ({
      name: `${r["Nº da Nota"] || 'S/N'} (${r["Nome do Projeto"] || 'Sem Proj'})`,
      atraso: parseDias(r["Dias em Atraso"]) || 0
    }))
    .sort((a, b) => b.atraso - a.atraso)
    .slice(0, 15);
  }, [filtrado]);

  // 4. Gráfico: Linha do Tempo de Valor Bruto por Data de Emissão
  const chartDataEmissao = useMemo(() => {
    const map = {};
    filtrado.forEach(r => {
      const dt = r["Emissão"];
      if (!dt) return;
      map[dt] = (map[dt] || 0) + (parseBRL(r["Valor Bruto"]) || 0);
    });
    return Object.entries(map)
      .map(([name, valor]) => ({ name, valor, obj: parseDataBr(name) }))
      .filter(x => x.obj)
      .sort((a, b) => a.obj - b.obj) // Ordena cronologicamente
      .map(x => ({ name: x.name, valor: x.valor }));
  }, [filtrado]);

  const location = useLocation();
  const isDashRoute = location.pathname.includes('-dash');
  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden", background: C.bg, fontFamily: "'DM Sans', 'Inter', system-ui, sans-serif", color: C.text }}>
      {isDashRoute ? (
        <SideBarNavDash isLight={isLight} />
      ) : (
        <SideBarNavFaturamento isLight={isLight} />
      )}

      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "auto" }}>
        {/* Header */}
        <header style={{
          background: "linear-gradient(90deg, #4c1d95 0%, #9d174d 100%)", color: "#fff", padding: "14px 24px",
          display: "flex", justifyContent: "space-between", alignItems: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.25)", flexShrink: 0
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 22, fontWeight: 800 }}>YOUX</span>
            <span style={{ fontSize: 18, fontWeight: 300 }}>GROUP</span>
            <span style={{ marginLeft: 12, fontSize: 13, opacity: 0.7 }}>/ Inadimplência</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {lastUpdate && <span style={{ fontSize: 11, opacity: 0.6 }}>Atualizado às {lastUpdate}</span>}
            <button onClick={() => setIsLight((l) => !l)} style={{
              display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)",
              borderRadius: 8, padding: "7px 12px", cursor: "pointer", color: "#fff", fontSize: 12, fontWeight: 600,
            }}>
              {isLight ? <Sun size={14} /> : <Moon size={14} />} {isLight ? "Tema Claro" : "Tema Escuro"}
            </button>
            <button onClick={() => navigate("/inicio")} style={{
              display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)",
              borderRadius: 8, padding: "7px 14px", cursor: "pointer", color: "#fff", fontSize: 13, fontWeight: 600,
            }}>
              Menu Principal
            </button>
          </div>
        </header>

        <main style={{ padding: "24px 28px", maxWidth: 1400, margin: "0 auto", width: "100%" }}>
          {/* KPIs */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14, marginBottom: 20 }}>
            <KpiCard icon={FileText} label="Total de Projetos" value={totalProjetosUnicos} C={C} color="#7C3AED" />
            <KpiCard icon={DollarSign} label="Valor Total" value={fmtBRL(totalValor)} C={C} color="#059669" sub={`${sorted.length} registros filtrados`} />
            <KpiCard icon={Clock} label="Média de Atraso" value={`${mediaAtraso} dias`} C={C} color="#F59E0B" />
            <KpiCard icon={AlertTriangle} label="Críticos (>60d)" value={criticos} sub={filtrado.length ? `${Math.round(criticos / filtrado.length * 100)}% do total` : "—"} C={C} color="#EF4444" />
          </div>

          {/* Filtros */}
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: 16, marginBottom: 20, boxShadow: isLight ? "0 1px 4px rgba(0,0,0,0.06)" : "0 1px 4px rgba(0,0,0,0.3)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <Filter size={14} color={C.muted} />
              <span style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>Filtros</span>
              {activeFilters > 0 && <span style={{ background: C.brand, color: "#fff", fontSize: 10, fontWeight: 700, borderRadius: 20, padding: "1px 7px" }}>{activeFilters} ativo(s)</span>}
            </div>
            
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 12 }}>
              <div>
                <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4, fontWeight: 600 }}>Cliente</label>
                <MultiSelect options={opts.cliente} placeholder="Cliente" value={filters.cliente} C={C} onChange={(v) => { setFilters((f) => ({ ...f, cliente: v })); setPage(1); }} />
              </div>
              <div>
                <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4, fontWeight: 600 }}>Gerente de Projeto</label>
                <MultiSelect options={opts.gp} placeholder="Gerente de Projeto" value={filters.gp} C={C} onChange={(v) => { setFilters((f) => ({ ...f, gp: v })); setPage(1); }} />
              </div>
              <DateFilter label="Data: Emissão" startValue={filters.emissaoInicio} endValue={filters.emissaoFim} 
                onChangeStart={(v) => setFilters(f => ({ ...f, emissaoInicio: v }))} 
                onChangeEnd={(v) => setFilters(f => ({ ...f, emissaoFim: v }))} C={C} />
              <DateFilter label="Data: Expectativa" startValue={filters.expectInicio} endValue={filters.expectFim} 
                onChangeStart={(v) => setFilters(f => ({ ...f, expectInicio: v }))} 
                onChangeEnd={(v) => setFilters(f => ({ ...f, expectFim: v }))} C={C} />
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8, border: `1px solid ${C.border}`, borderRadius: 7, padding: "7px 10px" }}>
                <Search size={13} color={C.muted} />
                <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Busca livre em todos os campos…" style={{ border: "none", outline: "none", background: "transparent", fontSize: 12, color: C.text, width: "100%" }} />
                {search && <button onClick={() => setSearch("")} style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}><X size={13} color={C.muted} /></button>}
              </div>
              {(activeFilters > 0 || search) && (
                <button onClick={limpar} style={{ fontSize: 13, color: C.brand, fontWeight: 700, background: "none", border: "none", cursor: "pointer", padding: 0, whiteSpace: "nowrap" }}>Limpar Filtros</button>
              )}
            </div>
          </div>

          {/* Gráficos (Recharts) */}
          {!loading && filtrado.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
              
              {/* Gráfico 1: Valor por Projeto */}
              <div style={{ background: C.card, padding: 16, borderRadius: 10, border: `1px solid ${C.border}` }}>
                <h3 style={{ fontSize: 13, color: C.muted, textTransform: "uppercase", marginBottom: 16 }}>Valor Inadimplente por Projeto</h3>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={chartDataProjeto} layout="vertical" margin={{ left: 60, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={C.chartGrid} />
                    <XAxis type="number" tickFormatter={(v) => `R$ ${v/1000}k`} stroke={C.muted} fontSize={11} />
                    <YAxis type="category" dataKey="name" width={100} stroke={C.muted} fontSize={10} />
                    <Tooltip formatter={(value) => [fmtBRL(value), "Valor Bruto"]} contentStyle={{ background: C.card, borderColor: C.border, color: C.text }} />
                    <Bar dataKey="valor" fill="#7C3AED" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Gráfico 2: Valor por GP */}
              <div style={{ background: C.card, padding: 16, borderRadius: 10, border: `1px solid ${C.border}` }}>
                <h3 style={{ fontSize: 13, color: C.muted, textTransform: "uppercase", marginBottom: 16 }}>Valor Inadimplente por Gerente</h3>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={chartDataGP} layout="vertical" margin={{ right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={C.chartGrid} />
                    <XAxis type="number" tickFormatter={(v) => `R$ ${v/1000}k`} stroke={C.muted} fontSize={11} />
                    <YAxis type="category" dataKey="name" width={90} stroke={C.muted} fontSize={10} />
                    <Tooltip formatter={(value) => [fmtBRL(value), "Valor Bruto"]} contentStyle={{ background: C.card, borderColor: C.border, color: C.text }} />
                    <Bar dataKey="valor" fill="#059669" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Gráfico 3: Dias de Atraso (Nota/Projeto) */}
              <div style={{ background: C.card, padding: 16, borderRadius: 10, border: `1px solid ${C.border}` }}>
                <h3 style={{ fontSize: 13, color: C.muted, textTransform: "uppercase", marginBottom: 16 }}>Dias de Atraso por Nota/Projeto</h3>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={chartDataAtraso} layout="vertical" margin={{ left: 20, right: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={C.chartGrid} />
                    <XAxis type="number" stroke={C.muted} fontSize={11} />
                    <YAxis type="category" dataKey="name" width={140} stroke={C.muted} fontSize={10} />
                    <Tooltip formatter={(value) => [`${value} dias`, "Atraso"]} contentStyle={{ background: C.card, borderColor: C.border, color: C.text }} />
                    <Bar dataKey="atraso" fill="#EF4444" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Gráfico 4: Evolução por Data de Emissão */}
              <div style={{ background: C.card, padding: 16, borderRadius: 10, border: `1px solid ${C.border}` }}>
                <h3 style={{ fontSize: 13, color: C.muted, textTransform: "uppercase", marginBottom: 16 }}>Evolução de Inadimplência por Emissão</h3>
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={chartDataEmissao} margin={{ left: 10, right: 20, top: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={C.chartGrid} />
                    <XAxis dataKey="name" stroke={C.muted} fontSize={10} />
                    <YAxis tickFormatter={(v) => `R$ ${v/1000}k`} stroke={C.muted} fontSize={11} />
                    <Tooltip formatter={(value) => [fmtBRL(value), "Valor Bruto"]} contentStyle={{ background: C.card, borderColor: C.border, color: C.text }} />
                    <Line type="monotone" dataKey="valor" stroke="#F59E0B" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

            </div>
          )}

          {/* Tabela */}
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden", boxShadow: isLight ? "0 1px 4px rgba(0,0,0,0.06)" : "0 1px 4px rgba(0,0,0,0.3)" }}>
            {loading ? (
              <div style={{ padding: 60, textAlign: "center", color: C.muted, fontSize: 14 }}>
                <RefreshCw size={20} style={{ animation: "spin 1s linear infinite", marginBottom: 10 }} /><br />
                Carregando dados de inadimplência…
              </div>
            ) : filtrado.length === 0 ? (
              <div style={{ padding: 60, textAlign: "center", color: C.muted, fontSize: 14 }}>
                Nenhum registro encontrado com os filtros aplicados.
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th colSpan={9} style={{ padding: "12px 14px 4px", textAlign: "left", borderBottom: "none" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div style={{ width: 4, height: 18, background: C.brand, borderRadius: 2 }} />
                          <span style={{ fontSize: 13, fontWeight: 800, color: C.text, textTransform: "uppercase", letterSpacing: "0.06em" }}>Tabela Analítica</span>
                        </div>
                      </th>
                    </tr>
                    <tr>
                      <ThSort label="Nº Nota" col="Nº da Nota" sort={sort} onSort={handleSort} />
                      <ThSort label="Cliente" col="Nome Cliente" sort={sort} onSort={handleSort} />
                      <ThSort label="Projeto" col="Nome do Projeto" sort={sort} onSort={handleSort} />
                      <ThSort label="GP" col="Gerente de Projeto" sort={sort} onSort={handleSort} />
                      <ThSort label="Emissão" col="Emissão" sort={sort} onSort={handleSort} />
                      <ThSort label="Expectativa" col="Expectativa recebimento" sort={sort} onSort={handleSort} />
                      <ThSort label="Dias em Atraso" col="Dias em Atraso" sort={sort} onSort={handleSort} align="center" />
                      <ThSort label="Situação NF" col="Situação NF" sort={sort} onSort={handleSort} align="center" />
                      <ThSort label="Valor Bruto" col="Valor Bruto" sort={sort} onSort={handleSort} align="right" />
                    </tr>
                  </thead>
                  <tbody>
                    {pageData.map((row, i) => {
                      const badge = situacaoBadge(row["Situação NF"], C);
                      const dias = parseDias(row["Dias em Atraso"]);
                      return (
                        <tr key={i}
                          style={{ background: i % 2 === 0 ? C.row1 : C.row2, transition: "background 0.15s" }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = isLight ? "#EDE9FA" : "#231545")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = i % 2 === 0 ? C.row1 : C.row2)}
                        >
                          <td style={{ padding: "10px 14px", fontSize: 12, color: C.muted, borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap" }}>{row["Nº da Nota"] || "—"}</td>
                          <td style={{ padding: "10px 14px", fontSize: 13, fontWeight: 600, color: C.text, borderBottom: `1px solid ${C.border}`, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row["Nome Cliente"] || "—"}</td>
                          <td style={{ padding: "10px 14px", fontSize: 12, color: C.text, borderBottom: `1px solid ${C.border}`, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row["Nome do Projeto"] || "—"}</td>
                          <td style={{ padding: "10px 14px", fontSize: 12, color: C.muted, borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap" }}>{row["Gerente de Projeto"] || "—"}</td>
                          <td style={{ padding: "10px 14px", fontSize: 12, color: C.muted, borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap" }}>{row["Emissão"] || "—"}</td>
                          <td style={{ padding: "10px 14px", fontSize: 12, color: C.muted, borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap" }}>{row["Expectativa recebimento"] || "—"}</td>
                          <td style={{ padding: "10px 14px", fontSize: 13, fontWeight: 700, borderBottom: `1px solid ${C.border}`, textAlign: "center", color: diasColor(row["Dias em Atraso"]) }}>{isNaN(dias) ? "—" : `${dias}d`}</td>
                          <td style={{ padding: "10px 14px", borderBottom: `1px solid ${C.border}`, textAlign: "center" }}>
                            <span style={{ background: badge.bg, color: badge.color, fontSize: 11, fontWeight: 700, borderRadius: 20, padding: "3px 9px", whiteSpace: "nowrap" }}>{badge.label}</span>
                          </td>
                          <td style={{ padding: "10px 14px", fontSize: 13, fontWeight: 700, borderBottom: `1px solid ${C.border}`, textAlign: "right", color: "#7C3AED", whiteSpace: "nowrap" }}>{fmtBRL(row["Valor Bruto"])}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: isLight ? "#EDE9FA" : "#231545" }}>
                      <td colSpan={8} style={{ padding: "10px 14px", fontSize: 12, fontWeight: 700, color: C.muted, borderTop: `2px solid ${C.border}` }}>TOTAL — {filtrado.length} registros</td>
                      <td style={{ padding: "10px 14px", fontSize: 13, fontWeight: 800, color: "#7C3AED", borderTop: `2px solid ${C.border}`, textAlign: "right", whiteSpace: "nowrap" }}>{fmtBRL(totalValor)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            {/* Paginação */}
            {!loading && filtrado.length > 0 && (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderTop: `1px solid ${C.border}` }}>
                <span style={{ fontSize: 12, color: C.muted }}>
                  Exibindo <strong style={{ color: C.text }}>{(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtrado.length)}</strong> de{" "}
                  <strong style={{ color: C.text }}>{filtrado.length}</strong> registros
                </span>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  {[
                    { label: "«", action: () => setPage(1), disabled: page === 1 },
                    { label: "‹", action: () => setPage((p) => p - 1), disabled: page === 1 },
                    { label: "›", action: () => setPage((p) => p + 1), disabled: page === totalPages },
                    { label: "»", action: () => setPage(totalPages), disabled: page === totalPages },
                  ].map((btn) => (
                    <button key={btn.label} onClick={btn.action} disabled={btn.disabled} style={{
                      padding: "5px 10px", borderRadius: 6, fontSize: 13, border: `1px solid ${C.border}`,
                      background: btn.disabled ? "transparent" : C.brand, color: btn.disabled ? C.muted : "#fff",
                      cursor: btn.disabled ? "not-allowed" : "pointer", fontWeight: 700,
                    }}>{btn.label}</button>
                  ))}
                  <span style={{ fontSize: 12, color: C.muted, marginLeft: 4 }}>Pág. {page}/{totalPages}</span>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}